import moment from 'moment';

type ApexFormatter = (value: number) => string;

type ApexWorld = {
  globals: {
    seriesX?: number[][];
    series?: number[][];
    seriesNames?: string[];
  };
  config: {
    colors?: string[];
    yaxis?: { labels?: { formatter?: ApexFormatter } }[];
  };
};

export type ApexTooltipContext = {
  seriesIndex: number;
  dataPointIndex: number;
  w: ApexWorld;
};

/**
 * Potência e SoC têm instantes próprios, então em quase todo ponto de potência
 * o SoC não tem medição no mesmo segundo. O último valor conhecido é o que a
 * task manda mostrar nesses instantes — a mesma regra que mantém a linha viva
 * até o fim da recarga. Devolve null enquanto a série ainda não mediu nada,
 * para que a linha do tooltip seja omitida em vez de escrever um traço.
 */
export const lastKnownValuesAt = (
  seriesX: number[][],
  series: number[][],
  hoveredX: number,
): (number | null)[] =>
  seriesX.map((timestamps, i) => {
    let lastKnown: number | null = null;

    for (let j = 0; j < timestamps.length; j += 1) {
      if (timestamps[j] > hoveredX) {
        break;
      }

      const value = series[i]?.[j];

      if (typeof value === 'number' && Number.isFinite(value)) {
        lastKnown = value;
      }
    }

    return lastKnown;
  });

const row = (color: string, name: string, value: string) =>
  '<div class="apexcharts-tooltip-series-group apexcharts-active" style="display: flex;">' +
  `<span class="apexcharts-tooltip-marker" style="background-color: ${color};"></span>` +
  '<div class="apexcharts-tooltip-text">' +
  '<div class="apexcharts-tooltip-y-group">' +
  `<span class="apexcharts-tooltip-text-y-label">${name}: </span>` +
  `<span class="apexcharts-tooltip-text-y-value">${value}</span>` +
  '</div></div></div>';

/**
 * O tooltip compartilhado do ApexCharts lê todas as séries com o mesmo índice
 * de ponto (printLabels: `w.globals.series[tIndex][j]`). Isso só funciona
 * quando as séries compartilham o eixo X; com pares (x, y) próprios o índice de
 * um ponto de potência cai numa medição de SoC de outro instante — o tooltip
 * mostrava um percentual de minutos adiante, ou um traço quando o índice passa
 * do fim da série mais curta. Aqui resolvemos cada série pelo x sob o cursor.
 */
export const buildPotencyChartTooltipHtml = ({
  seriesIndex,
  dataPointIndex,
  w,
}: ApexTooltipContext): string => {
  const seriesX = w.globals.seriesX ?? [];
  const series = w.globals.series ?? [];
  const names = w.globals.seriesNames ?? [];
  const colors = w.config.colors ?? [];
  const axes = w.config.yaxis ?? [];

  const hoveredX =
    seriesX[seriesIndex]?.[dataPointIndex] ?? seriesX[0]?.[dataPointIndex];

  if (hoveredX == null) {
    return '';
  }

  const title = `<div class="apexcharts-tooltip-title">${moment(
    hoveredX,
  ).format('DD/MM/YYYY HH:mm:ss')}</div>`;

  const rows = lastKnownValuesAt(seriesX, series, hoveredX)
    .map((value, i) => {
      if (value == null) {
        return '';
      }

      const formatter = axes[i]?.labels?.formatter;

      return row(
        colors[i] ?? '',
        names[i] ?? '',
        formatter ? formatter(value) : String(value),
      );
    })
    .join('');

  return `${title}${rows}`;
};

export const potencyChartTooltip = {
  shared: true,
  custom: buildPotencyChartTooltipHtml,
};

/**
 * `tooltip: undefined` derruba o gráfico. O Utils.extend do ApexCharts percorre
 * Object.keys(source), então a chave existe mesmo valendo undefined e sobrescreve
 * o objeto default inteiro; o handleUserInputErrors logo em seguida lê
 * config.tooltip.shared e estoura ainda no construtor — antes de qualquer
 * render. Isso atingiria toda transação sem SoC, que é a maioria.
 *
 * Sem SoC devolvemos o default explícito. `custom: undefined` é o valor de
 * fábrica (o ApexCharts testa `tooltip.custom !== undefined`) e, sendo uma chave
 * presente, também limpa um custom que tenha sobrado: o react-apexcharts
 * atualiza por updateOptions, que faz merge sobre a config anterior, então um
 * objeto vazio não apagaria o custom de uma transação com SoC exibida antes no
 * mesmo card.
 */
export const potencyTooltipFor = (hasSoc: boolean) =>
  hasSoc ? potencyChartTooltip : { custom: undefined };
